import { Context } from "../services/models/Context";
import { SendWhatsappMessage } from "../services/WhatsappService";
import { STATUS_TRANSITION_TEMPLATE_MAP } from "../pages/OPD/OPDClinical/OPDClinical.constants";
import { PreferencesContext } from "../PreferenceContext";
import { useContext } from "react";
import { AlertType } from "../services/models/AlertType";
import { format, parse } from "date-fns";

export type StatusTransitionKey =
  | "SCHEDULED_TO_WAITING"
  | "WAITING_TO_CONSULTING"
  | "CONSULTING_TO_COMPLETE";

export type WaModuleType = "MANUAL" | "VISTAMED" | "CLINIC_API" | null;

/** Normalise an Indian mobile number to 91XXXXXXXXXX format */
export function normalizeIndianPhone(raw?: string): string | null {
  if (!raw) return null;
  const phone = raw
    .replace(/[\s\-\+]/g, "")
    .replace(/^0+/, "")
    .replace(/^91/, "")
    .replace(/^/, "91");
  return phone || null;
}

/** Extract short token from a /r?t=... URL if present */
export function shortTokenFromUrl(url?: string): string {
  if (!url) return "";
  const parts = url.split("r?t=");
  return parts.length > 1 ? parts[1] || "" : "";
}

export type OpdTokenNeed =
  | "prescription"
  | "fullPrescription"
  | "growthChart"
  | "billing";

/**
 * Ensure OPD short-link tokens exist before WhatsApp/email send.
 * List SP no longer returns tokens — generate only for the rows being sent.
 */
export async function ensureOpdSendTokens(
  item: any,
  generateTokenFn: (data: any) => Promise<any>,
  needs: OpdTokenNeed[] = [
    "prescription",
    "fullPrescription",
    "growthChart",
    "billing",
  ],
): Promise<any> {
  let curr = { ...item };
  if (!curr?.opdId && !curr?.billingId) return curr;

  if (needs.includes("prescription") && curr.opdId) {
    if (!curr.token) curr.token = shortTokenFromUrl(curr.prescriptionUrl);
    if (!curr.token) {
      const t = await generateTokenFn({
        type: "PRESCRIPTION",
        actionType: "PRESCRIPTION_LINK",
        redirectPath: "/prescription",
        remarks: "Prescription Link",
        linkId: curr.opdId,
        tableName: "OPD",
      });
      curr.token =
        t?.shortCode ?? shortTokenFromUrl(t?.targetUrl) ?? "";
      curr.prescriptionUrl = t?.targetUrl ?? curr.prescriptionUrl ?? "";
    }
  }

  if (needs.includes("fullPrescription") && curr.opdId) {
    if (!curr.fullPrescriptionToken)
      curr.fullPrescriptionToken = shortTokenFromUrl(curr.fullPrescriptionUrl);
    if (!curr.fullPrescriptionToken) {
      const t = await generateTokenFn({
        type: "FULL_PRESCRIPTION",
        actionType: "FULL_PRESCRIPTION_LINK",
        redirectPath: "/prescription-report",
        remarks: "Full Prescription Link",
        linkId: curr.opdId,
        tableName: "Prescription",
      });
      curr.fullPrescriptionToken =
        t?.shortCode ?? shortTokenFromUrl(t?.targetUrl) ?? "";
      curr.fullPrescriptionUrl =
        t?.targetUrl ?? curr.fullPrescriptionUrl ?? "";
    }
  }

  if (needs.includes("growthChart") && curr.opdId) {
    if (!curr.growthChartToken)
      curr.growthChartToken = shortTokenFromUrl(curr.growthChartUrl);
    if (!curr.growthChartToken) {
      const t = await generateTokenFn({
        type: "GROWTH_CHART",
        actionType: "GROWTH_CHART_LINK",
        redirectPath: "/growth-chart-report",
        remarks: "Growth Chart Link",
        linkId: curr.opdId,
        tableName: "GrowthChart",
      });
      curr.growthChartToken =
        t?.shortCode ?? shortTokenFromUrl(t?.targetUrl) ?? "";
      curr.growthChartUrl = t?.targetUrl ?? curr.growthChartUrl ?? "";
    }
  }

  if (needs.includes("billing") && curr.billingId) {
    if (!curr.billToken) curr.billToken = shortTokenFromUrl(curr.billUrl);
    if (!curr.billToken) {
      const t = await generateTokenFn({
        type: "BILLING",
        actionType: "BILLING_LINK",
        redirectPath: "/billing-shared",
        remarks: "Billing Link",
        linkId: curr.billingId,
        tableName: "Billing",
      });
      curr.billToken =
        t?.shortCode ?? shortTokenFromUrl(t?.targetUrl) ?? "";
      curr.billUrl = t?.targetUrl ?? curr.billUrl ?? "";
    }
  }

  return curr;
}

/** Map visit-completion template style → which tokens to ensure */
export function tokenNeedsForTemplateType(typeKey?: string): OpdTokenNeed[] {
  switch (typeKey) {
    case "WITHOUT_PRESCRIPTION":
      return [];
    case "WITH_PRESCRIPTION_AND_BILL":
      return ["fullPrescription", "billing"];
    case "WITH_PRESCRIPTION":
    default:
      return ["fullPrescription"];
  }
}

/**
 * Resolve template parameter values from an OPD item + userContext.
 * Covers all parameter names used across STATUS_TRANSITION_TEMPLATE_MAP,
 * VISIT_COMPLETION_TEMPLATE_MAP, and REFERRAL_VISIT_COMPLETION_TEMPLATE_MAP.
 */
export function resolveTemplateParams(
  item: any,
  parameterNames: string[],
  userContext: Context,
  options?: { useSplitForAppointmentUrl?: boolean },
): string[] {
  const fullRxToken =
    item.fullPrescriptionToken ||
    shortTokenFromUrl(item.fullPrescriptionUrl) ||
    "";
  const billTok = item.billToken || shortTokenFromUrl(item.billUrl) || "";
  const rxToken =
    item.token || shortTokenFromUrl(item.prescriptionUrl) || fullRxToken;

  return parameterNames.map((name) => {
    switch (name) {
      case "patientName":
        return item.patientName || "";
      case "consultingDoctorName":
        return item.consultingDoctorName || "";
      case "organizationName":
        return item.locationName || "";
      // case "organizationName":
      //   return userContext.organizationName || "";
      case "branchMobile":
        return userContext.branchMobile || "";
      case "appointmentNumber":
        return item.appointmentNumber || "";
      case "diagnosisSummary":
        try {
          const diagnosis = JSON.parse(item.diagnosisSummary || "[]");

          return diagnosis.length
            ? diagnosis.map((d: any) => d.diagnosisName).join(", ")
            : "N/A";
        } catch {
          return "N/A";
        }
      case "referredByName":
        return item.referredByName || "";

      case "appointmentDateTime":
        return item.caseDateTime || "";
      case "appointmentDate":
        return (
          format(
            parse(item.appointmentDatetime, "dd-MM-yyyy hh:mm a", new Date()),
            "dd-MM-yyyy",
          ) || ""
        );

      /* link */

      /* appointment */
      case "appointmentUrl":
        return userContext.appointmentUrl || "";
      case "fullAppointmentUrl":
        return options?.useSplitForAppointmentUrl
          ? "r?t=" + (userContext.appointmentUrl?.split("r?t=")[1] || "")
          : userContext.appointmentUrl || "";
      case "appointmentLink":
        return userContext.appointmentLink || " ";

      /* Prescription */
      case "fullPrescriptionUrl":
        return "r?t=" + (fullRxToken || " ");
      case "prescriptionToken":
        return "r?t=" + (rxToken || "");
      case "fullPrescriptionLink":
        return item.fullPrescriptionUrl || (fullRxToken ? "r?t=" + fullRxToken : " ");

      /* Referral */
      case "referralBookLink":
        return "r?t=" + (userContext.referBookUrl?.split("r?t=")[1] || "");

      /* Billing */
      case "billingUrl":
        return "r?t=" + (billTok || "");

      /* default */
      default:
        return "";
    }
  });
}

/**
 * useOpdWhatsApp
 *
 * Returns `sendStatusTransitionWhatsApp` — a fire-and-forget helper that
 * sends a WhatsApp template message when an OPD patient moves between statuses.
 * Works only in API mode (VISTAMED / CLINIC_API); silently skips MANUAL mode.
 *
 * Usage:
 *   const { sendStatusTransitionWhatsApp } = useOpdWhatsApp(userContext, waModuleType);
 *   await sendStatusTransitionWhatsApp(opdItem, "SCHEDULED_TO_WAITING");
 */
export function useOpdWhatsApp(
  // userContext: Context,
  waModuleType: WaModuleType,
) {
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const sendStatusTransitionWhatsApp = async (
    item: any,
    transitionKey: StatusTransitionKey,
  ): Promise<void> => {
    if (waModuleType === "MANUAL") return;

    const phone = normalizeIndianPhone(item?.patientPrimaryMobileNumber);
    if (!phone) return;
    let result: any = "";
    try {
      const lang =
        item.preferredLanguageName?.toLowerCase() ||
        item.patientPreferredLanguageName?.toLowerCase() ||
        "english";
      const templateConfig = STATUS_TRANSITION_TEMPLATE_MAP[transitionKey];
      const template =
        templateConfig.languages[lang] || templateConfig.languages.english;

      const payload = {
        branchId: userContext.branchId,
        consumerId: userContext.consumerId,
        orgId: userContext.organizationId,
        userId: userContext.loginDetail.userId,
        number: phone,
        messageType: "TEMPLATE",
        templateName: template.templateName,
        templateLanguage: template.langCode,
        templateParameters: resolveTemplateParams(
          item,
          template.parameters,
          userContext,
        ),
      };
      result = await SendWhatsappMessage(payload);
      if (result?.success) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      }
    } catch {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          result?.message || `Failed to send WhatsApp to ${item.fullName}.`,
        alertType: AlertType.Error,
      });
    }
  };

  return { sendStatusTransitionWhatsApp };
}
